import { escapeHtml } from '../ui.js';
import { ensureScopedToken, findDriveChild, createDriveFolder, findDriveFileByCaptureId, uploadToDrive } from '../google-api.js';
import { saveSiteVisit, addSiteDocument, updateProjectActivity } from '../db.js';
import { makeLocalId, saveVisit, getVisit, saveItem, itemsForVisit, pendingVisits, requestDurableStorage } from '../site-visit-store.js';
import { callFunction, orgPath } from '../db.js';
import { visitPrompts } from './site-checks-surfaces.js';

const DEFAULT_TAGS = ['Front elevation', 'Rear', 'Left', 'Right', 'Internal', 'Defect', 'Existing structure', 'Services', 'Access'];
const PURPOSES = ['Initial survey', 'Inspection', 'Progress', 'Investigation'];
const cleanPart = (s) => String(s || '').trim().replace(/[^A-Za-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 42);
const dateKey = (iso) => String(iso).slice(0, 10);
const extFor = (item) => {
  const fromName = String(item.originalName || '').match(/\.([a-zA-Z0-9]{2,5})$/)?.[1];
  if (fromName) return fromName.toLowerCase();
  if (item.mimeType.startsWith('audio/')) return item.mimeType.includes('mp4') ? 'm4a' : 'webm';
  if (item.mimeType.startsWith('video/')) return 'mp4';
  return 'jpg';
};

async function findOrCreateFolder(parentId, name) {
  return await findDriveChild(parentId, name) || await createDriveFolder({ name, parentId });
}

async function syncVisit(visitId, onStatus = () => {}) {
  let visit = await getVisit(visitId);
  if (!visit || !navigator.onLine) return false;
  onStatus('Connecting to Drive…');
  await ensureScopedToken('https://www.googleapis.com/auth/drive');
  // Site-visit media files into the project's standard "7. Images" folder, in a
  // subfolder named for the visit date only (Karan 15/07 — was landing in a
  // separate "Site Visits" tree). "7. Images" already exists on every project,
  // so findDriveChild matches it rather than creating a duplicate.
  const images = await findOrCreateFolder(visit.folderId, '7. Images');
  const folder = await findOrCreateFolder(images.id, dateKey(visit.startedAt));
  visit = { ...visit, driveFolderId: folder.id, driveFolderUrl: folder.webViewLink || '', state: 'uploading', updatedAt: new Date().toISOString() };
  await saveVisit(visit);
  await saveSiteVisit(visit.projectId, visit.id, { ...visit, itemCount: undefined });

  const items = await itemsForVisit(visit.id);
  for (const item of items) {
    if (item.status === 'uploaded' && item.driveId) continue;
    onStatus(`Filing ${item.seq} of ${items.length}…`);
    let drive = await findDriveFileByCaptureId(folder.id, item.id);
    const tag = cleanPart(item.tag);
    const kind = item.kind === 'audio' ? 'VOICE' : 'SV';
    const name = `${visit.projectNo}-${kind}-${dateKey(visit.startedAt)}-${String(item.seq).padStart(3, '0')}${tag ? '_' + tag : ''}.${extFor(item)}`;
    if (!drive) drive = await uploadToDrive({ name, blob: item.blob, parentId: folder.id, appProperties: { trackCaptureId: item.id, trackVisitId: visit.id } });
    const done = { ...item, status: 'uploaded', filename: name, driveId: drive.id, driveUrl: drive.webViewLink || '', uploadedAt: new Date().toISOString(), error: null };
    await addSiteDocument(item.id, {
      projectNo: String(visit.projectNo), projectRef: orgPath(`projects/${visit.projectId}`),
      visitId: visit.id, filename: name, driveId: drive.id, driveUrl: drive.webViewLink || '',
      direction: 'internal', type: 'site-media', category: item.kind === 'audio' ? 'audio' : 'image',
      status: 'current', tag: item.tag || null, caption: item.caption || null,
      createdBy: visit.engineerEmail, createdAt: item.createdAt, updatedAt: new Date().toISOString(), needsReview: false,
    });
    await saveItem(done);
  }
  visit = { ...visit, state: 'filed', filedAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
  await saveSiteVisit(visit.projectId, visit.id, visit);
  await updateProjectActivity(visit.projectId, visit.engineerEmail);
  await saveVisit(visit);
  onStatus('Everything filed ✓');
  return true;
}

export async function openSiteVisit({ project, session, onComplete }) {
  if (!project?.folderId) throw new Error('This project has no linked Drive folder.');
  await requestDurableStorage();
  const recoverable = (await pendingVisits()).filter((v) => v.projectId === project.id && v.engineerEmail === session.email)
    .sort((a, b) => String(b.startedAt).localeCompare(String(a.startedAt)))[0];
  const startedAt = recoverable?.startedAt || new Date().toISOString();
  let visit = recoverable ? { ...recoverable, state: 'capturing', updatedAt: new Date().toISOString() } : {
    id: makeLocalId(), projectId: project.id, projectNo: String(project.number || ''),
    projectName: project.name || project.clientName || '', folderId: project.folderId,
    engineerEmail: session.email, engineerName: session.displayName, startedAt,
    purpose: '', weather: '', state: 'capturing', updatedAt: startedAt,
  };
  await saveVisit(visit);

  // PROMPTS FROM THE SITE CHECK (design_handoff_site_checks §6). The desktop
  // visit has no checklist of its own, so the flags' prompts ride on the visit
  // record as `sitePrompts` [{ id, text, from, done }] — held on the device with
  // everything else and filed by syncVisit with the visit (saveSiteVisit writes
  // the whole visit). Read once, when the visit starts, and only with signal:
  // a visit opened offline simply has no prompts. Never blocks the camera.
  if (!Array.isArray(visit.sitePrompts) && navigator.onLine) {
    const snap = await Promise.race([
      callFunction('getSiteChecks', { projectId: project.id, limit: 1 }).then((r) => (r && r.latest) || null).catch(() => null),
      new Promise((r) => setTimeout(() => r(null), 4000)),
    ]);
    const prompts = visitPrompts(snap);
    if (prompts.length) {
      visit = { ...visit, sitePrompts: prompts.map((p) => ({ ...p, done: false })), updatedAt: new Date().toISOString() };
      await saveVisit(visit);
    }
  }

  const screen = document.createElement('div');
  screen.className = 'sv-screen';
  document.body.appendChild(screen);
  let recording = null, chunks = [], stream = null;

  const render = async (message = '') => {
    visit = await getVisit(visit.id);
    const items = await itemsForVisit(visit.id);
    screen.innerHTML = `<header class="sv-head"><div><span>Site visit</span><strong>${escapeHtml(visit.projectNo)} · ${escapeHtml(visit.projectName)}</strong></div><button class="sv-close" aria-label="Close">×</button></header>
      <main class="sv-main">
        <section class="sv-setup"><div class="sv-label">Purpose (optional)</div><div class="sv-chips">${PURPOSES.map((p) => `<button data-purpose="${escapeHtml(p)}" class="${visit.purpose === p ? 'active' : ''}">${escapeHtml(p)}</button>`).join('')}</div>
          <input class="sv-weather" placeholder="Weather note (optional)" value="${escapeHtml(visit.weather)}"></section>
        ${Array.isArray(visit.sitePrompts) && visit.sitePrompts.length ? `<section class="sv-setup"><div class="sv-label">Prompts from the site check</div>
          <div class="sv-prompts">${visit.sitePrompts.map((p, i) => `<label class="sv-prompt"><input type="checkbox" data-prompt="${i}"${p.done ? ' checked' : ''}><span>${escapeHtml(p.text)}<small>${escapeHtml(p.from || '')}</small></span></label>`).join('')}</div></section>` : ''}
        <div class="sv-status"><strong>${items.length}</strong> captured · ${navigator.onLine ? 'online' : 'saved offline'}${message ? `<span>${escapeHtml(message)}</span>` : ''}</div>
        <div class="sv-actions"><button class="sv-action photo" id="sv-photo"><b>📷</b><span>Photo / Video</span></button><button class="sv-action voice ${recording ? 'recording' : ''}" id="sv-voice"><b>🎙</b><span>${recording ? 'Stop recording' : 'Voice note'}</span></button></div>
        <input id="sv-file" type="file" accept="image/*,video/*" capture="environment" hidden>
        <section class="sv-captures">${items.slice().reverse().map((it) => `<article class="sv-item" data-id="${it.id}"><div class="sv-item-icon">${it.kind === 'audio' ? '🎙' : it.mimeType.startsWith('video/') ? '🎥' : '📷'}</div><div><strong>Capture ${it.seq}</strong><small>${escapeHtml(it.tag || 'Untagged')} · ${it.status === 'uploaded' ? 'Filed' : 'Safe on device'}</small><div class="sv-chips compact">${DEFAULT_TAGS.map((t) => `<button data-tag="${escapeHtml(t)}" class="${it.tag === t ? 'active' : ''}">${escapeHtml(t)}</button>`).join('')}</div><input class="sv-caption" placeholder="Optional caption" value="${escapeHtml(it.caption || '')}"></div></article>`).join('')}</section>
      </main><footer class="sv-foot"><button id="sv-end" class="sv-end">✓ End visit</button><button id="sv-sync" class="sv-sync">Sync now</button></footer>`;
    screen.querySelector('.sv-close').onclick = () => { if (recording) recording.stop(); screen.remove(); };
    screen.querySelectorAll('[data-purpose]').forEach((b) => b.onclick = async () => { visit = { ...visit, purpose: b.dataset.purpose, updatedAt: new Date().toISOString() }; await saveVisit(visit); render(); });
    screen.querySelector('.sv-weather').onchange = async (e) => { visit = { ...visit, weather: e.target.value.trim(), updatedAt: new Date().toISOString() }; await saveVisit(visit); };
    screen.querySelectorAll('[data-prompt]').forEach((box) => box.onchange = async () => {
      const i = Number(box.dataset.prompt);
      const sitePrompts = (visit.sitePrompts || []).map((p, k) => (k === i ? { ...p, done: box.checked } : p));
      visit = { ...visit, sitePrompts, updatedAt: new Date().toISOString() };
      await saveVisit(visit);
    });
    const file = screen.querySelector('#sv-file');
    screen.querySelector('#sv-photo').onclick = () => file.click();
    file.onchange = async () => {
      let seq = items.length;
      for (const blob of file.files) await saveItem({ id: makeLocalId(), visitId: visit.id, seq: ++seq, kind: blob.type.startsWith('video/') ? 'video' : 'photo', mimeType: blob.type || 'image/jpeg', originalName: blob.name, blob, tag: '', caption: '', status: 'queued', createdAt: new Date().toISOString() });
      render('Saved instantly');
    };
    screen.querySelector('#sv-voice').onclick = async () => {
      if (recording) { recording.stop(); return; }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        chunks = []; recording = new MediaRecorder(stream);
        recording.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
        recording.onstop = async () => {
          const blob = new Blob(chunks, { type: recording.mimeType || 'audio/webm' });
          recording = null; stream.getTracks().forEach((t) => t.stop()); stream = null;
          const latest = await itemsForVisit(visit.id);
          await saveItem({ id: makeLocalId(), visitId: visit.id, seq: latest.length + 1, kind: 'audio', mimeType: blob.type, originalName: '', blob, tag: '', caption: '', status: 'queued', createdAt: new Date().toISOString() });
          render('Voice note saved');
        };
        recording.start(); render();
      } catch (e) { render(`Microphone unavailable: ${e.message || e}`); }
    };
    screen.querySelectorAll('.sv-item').forEach((card) => {
      card.querySelectorAll('[data-tag]').forEach((b) => b.onclick = async () => { const it = (await itemsForVisit(visit.id)).find((x) => x.id === card.dataset.id); await saveItem({ ...it, tag: it.tag === b.dataset.tag ? '' : b.dataset.tag }); render(); });
      card.querySelector('.sv-caption').onchange = async (e) => { const it = (await itemsForVisit(visit.id)).find((x) => x.id === card.dataset.id); await saveItem({ ...it, caption: e.target.value.trim() }); };
    });
    const doSync = async () => { try { await syncVisit(visit.id, (s) => { const n = screen.querySelector('.sv-status span'); if (n) n.textContent = s; }); await render('Everything filed ✓'); } catch (e) { await render(`Safe on device · ${e.message || e}`); } };
    screen.querySelector('#sv-sync').onclick = doSync;
    screen.querySelector('#sv-end').onclick = async () => {
      if (recording) { recording.stop(); await render('Voice note stopping · tap End visit again'); return; }
      visit = { ...visit, state: 'ended_pending_sync', endedAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
      await saveVisit(visit);
      if (navigator.onLine) await doSync(); else await render('Visit ended · waiting for signal');
      if ((await getVisit(visit.id)).state === 'filed') { screen.remove(); onComplete?.(); }
    };
  };
  await render();
}

export { syncVisit };

// Best-effort background resume. Mobile browsers may suspend the PWA, so the
// same retry also runs whenever this module is loaded again.
async function resumePending() {
  if (!navigator.onLine) return;
  const visits = await pendingVisits().catch(() => []);
  for (const visit of visits.filter((v) => v.state === 'ended_pending_sync' || v.state === 'uploading')) {
    try { await syncVisit(visit.id); } catch (_) { /* remains durable for next retry/manual Sync */ }
  }
}
window.addEventListener('online', resumePending);
setTimeout(resumePending, 1200);
